/**
 * Route chunk prefetching.
 *
 * Each merchant route is code-split (see router.tsx). The browser only fetches a
 * page's JS chunk the first time it is needed, which shows a brief loader. By
 * kicking off the same dynamic import on hover/focus we warm the module cache so
 * the actual navigation resolves instantly ("serve cached version").
 *
 * The import specifiers MUST stay byte-identical to the ones in router.tsx so
 * Vite emits a single shared chunk and the browser dedupes the request.
 */

type Loader = () => Promise<unknown>;

const loaders: Record<string, Loader> = {
  '/': () => import('../pages/dashboard/Dashboard'),
  '/catalog': () => import('../pages/catalog/CatalogList'),
  '/catalog/new': () => import('../pages/catalog/ProductForm'),
  '/catalog/import': () => import('../pages/catalog/BulkImport'),
  '/ai-settings': () => import('../pages/ai-settings/AISettings'),
  '/conversations': () => import('../pages/conversations/Conversations'),
  '/orders': () => import('../pages/orders/Orders'),
  '/bookings': () => import('../pages/bookings/Bookings'),
  '/availability': () => import('../pages/availability/Availability'),
  '/staff': () => import('../pages/staff/Staff'),
  '/payments': () => import('../pages/payments/Payments'),
  '/analytics': () => import('../pages/analytics/Analytics'),
  '/delivery': () => import('../pages/delivery/Delivery'),
  '/billing': () => import('../pages/billing/Billing'),
  '/settings': () => import('../pages/settings/SettingsLayout'),
  '/settings/channels': () => import('../pages/channels/Channels'),
  '/settings/notifications': () => import('../pages/settings/Notifications'),

  // Super-admin console
  '/superadmin': () => import('../pages/superadmin/Overview'),
  '/superadmin/tenants': () => import('../pages/superadmin/Tenants'),
  '/superadmin/users': () => import('../pages/superadmin/Users'),
  '/superadmin/conversations': () => import('../pages/superadmin/Conversations'),
  '/superadmin/orders': () => import('../pages/superadmin/Orders'),
  '/superadmin/products': () => import('../pages/superadmin/Products'),
};

// Only fire each import once — the browser caches the module afterwards.
const warmed = new Set<string>();

export function prefetchRoute(path: string): void {
  if (warmed.has(path)) return;
  const load = loaders[path];
  if (!load) return;
  warmed.add(path);
  // Swallow errors: a failed prefetch must never surface to the user; the real
  // navigation will retry (and lazyWithRetry handles stale-deploy recovery).
  void load().catch(() => warmed.delete(path));
}
